const vertexSource = `#version 300 es
in vec2 position;
uniform vec2 resolution;
uniform vec4 rect;
uniform vec4 uvRect;
out vec2 uv;
out vec2 point;
void main() {
  point = rect.xy + position * rect.zw;
  uv = uvRect.xy + position * uvRect.zw;
  gl_Position = vec4(point / resolution * vec2(2.0, -2.0) + vec2(-1.0, 1.0), 0.0, 1.0);
}`;

const fragmentSource = `#version 300 es
precision highp float;
uniform sampler2D source;
uniform bool textured;
uniform vec4 fill;
uniform float opacity;
uniform vec4 shape;
uniform float radius;
in vec2 uv;
in vec2 point;
out vec4 color;
void main() {
  vec2 halfSize = shape.zw * 0.5;
  vec2 q = abs(point - shape.xy - halfSize) - halfSize + radius;
  float distance = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - radius;
  float edge = max(fwidth(distance) * 0.5, 0.0001);
  float coverage = 1.0 - smoothstep(-edge, edge, distance);
  color = (textured ? texture(source, uv) : fill) * opacity * coverage;
}`;

export const createDomScene = gl => {
  const compile = (type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(message);
    }
    return shader;
  };
  const program = gl.createProgram();
  const shaders = [compile(gl.VERTEX_SHADER, vertexSource), compile(gl.FRAGMENT_SHADER, fragmentSource)];
  for (const shader of shaders) gl.attachShader(program, shader);
  gl.linkProgram(program);
  for (const shader of shaders) gl.deleteShader(shader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
  const vao = gl.createVertexArray();
  const buffer = gl.createBuffer();
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'position');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  const locations = Object.fromEntries(['resolution', 'rect', 'uvRect', 'source', 'textured', 'fill', 'opacity', 'shape', 'radius'].map(name => [name, gl.getUniformLocation(program, name)]));
  const textures = new Set();
  const layers = new Set();
  const colors = new Map();
  const colorCanvas = document.createElement('canvas');
  colorCanvas.width = colorCanvas.height = 1;
  const colorContext = colorCanvas.getContext('2d', { willReadFrequently: true });
  const color = value => {
    if (!colors.has(value)) {
      colorContext.clearRect(0, 0, 1, 1);
      colorContext.fillStyle = value;
      colorContext.fillRect(0, 0, 1, 1);
      const [r, g, b, a] = colorContext.getImageData(0, 0, 1, 1).data;
      const alpha = a / 255;
      colors.set(value, [r / 255 * alpha, g / 255 * alpha, b / 255 * alpha, alpha]);
    }
    return colors.get(value);
  };
  const createTexture = () => {
    const resource = { texture: gl.createTexture(), width: 0, height: 0, version: 0 };
    textures.add(resource);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, resource.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return resource;
  };
  const upload = (resource, source) => {
    const width = source.videoWidth || source.naturalWidth || source.width;
    const height = source.videoHeight || source.naturalHeight || source.height;
    if (!width || !height) return;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, resource.texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    if (resource.width !== width || resource.height !== height) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      resource.width = width;
      resource.height = height;
    } else {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, source);
    }
    resource.version++;
  };
  const release = resource => {
    gl.deleteTexture(resource.texture);
    textures.delete(resource);
  };
  const fallback = createTexture();
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([255, 255, 255, 255]));
  const createLayer = () => {
    const layer = createTexture();
    textures.delete(layer);
    layer.framebuffer = gl.createFramebuffer();
    layers.add(layer);
    return layer;
  };
  const resizeLayer = (layer, width, height) => {
    if (layer.width === width && layer.height === height) return;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, layer.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, layer.texture, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) throw new Error('DOM framebuffer is incomplete');
    layer.width = width;
    layer.height = height;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  };
  const draw = (layer, commands, width, height, background) => {
    gl.bindFramebuffer(gl.FRAMEBUFFER, layer.framebuffer);
    gl.viewport(0, 0, layer.width, layer.height);
    gl.disable(gl.SCISSOR_TEST);
    gl.clearColor(...(background ? color(background) : [0, 0, 0, 0]));
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(program);
    gl.bindVertexArray(vao);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.SCISSOR_TEST);
    gl.uniform2f(locations.resolution, width, height);
    gl.uniform1i(locations.source, 0);
    gl.activeTexture(gl.TEXTURE0);
    const dx = layer.width / width;
    const dy = layer.height / height;
    for (const command of commands) {
      const { rect, clip, resource, opacity, radius = 0, shape = rect, uv = [0, 0, 1, 1] } = command;
      if (resource && !resource.version) continue;
      const left = Math.max(0, Math.ceil(clip.left * dx));
      const right = Math.min(layer.width, Math.floor(clip.right * dx));
      const top = Math.max(0, Math.ceil(clip.top * dy));
      const bottom = Math.min(layer.height, Math.floor(clip.bottom * dy));
      if (right <= left || bottom <= top) continue;
      gl.scissor(left, layer.height - bottom, right - left, bottom - top);
      gl.uniform4f(locations.rect, rect.left, rect.top, rect.width, rect.height);
      gl.uniform4f(locations.shape, shape.left, shape.top, shape.width, shape.height);
      gl.uniform4f(locations.uvRect, ...uv);
      gl.uniform1f(locations.opacity, opacity);
      gl.uniform1f(locations.radius, Math.min(radius, shape.width / 2, shape.height / 2));
      gl.uniform1i(locations.textured, resource ? 1 : 0);
      gl.bindTexture(gl.TEXTURE_2D, resource ? resource.texture : fallback.texture);
      if (!resource) gl.uniform4f(locations.fill, ...color(command.fill));
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    layer.version++;
  };
  return {
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
    createTexture, upload, release, createLayer, resizeLayer, draw,
    destroy() {
      for (const resource of textures) gl.deleteTexture(resource.texture);
      for (const layer of layers) {
        gl.deleteTexture(layer.texture);
        gl.deleteFramebuffer(layer.framebuffer);
      }
      textures.clear();
      layers.clear();
      gl.deleteBuffer(buffer);
      gl.deleteVertexArray(vao);
      gl.deleteProgram(program);
    },
  };
};
